import { memo } from 'react';
import {
    Box,
    Flex,
    Grid,
    Text,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    ModalCloseButton,
    VStack,
    HStack,
    Divider,
    NumberInput,
    NumberInputField,
    PinInput,
    PinInputField,
    Heading,
} from '@chakra-ui/react';
import { addCommasToNumber } from 'utils/utils';
import {
    FLOW_STEP,
    WITHDRAWAL_METHODS,
    TIER_LIMITS,
} from 'hooks/useWithdrawalFlow';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */

const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const SUCCESS     = '#10B981';
const SUCCESS_BG  = '#ECFDF5';
const DANGER      = '#E53E3E';
const DANGER_BG   = '#FEF2F2';
const WARNING     = '#EA580C';
const WARNING_BG  = '#FFF7ED';
const INFO        = '#2563EB';
const INFO_BG     = '#EFF6FF';

const FONT = "'Bricolage Grotesque', sans-serif";

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */

const CheckCircleIcon = (props) => (
    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" {...props}>
        <circle cx="12" cy="12" r="11" fill={SUCCESS_BG} stroke={SUCCESS} strokeWidth="1.5" />
        <path d="M7 12l3.5 3.5L17 9" stroke={SUCCESS} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const XCircleIcon = (props) => (
    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" {...props}>
        <circle cx="12" cy="12" r="11" fill={DANGER_BG} stroke={DANGER} strokeWidth="1.5" />
        <path d="M9 9l6 6M9 15l6-6" stroke={DANGER} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
);

const LockIcon = (props) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" {...props}>
        <rect x="4" y="11" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 11V7a4 4 0 018 0v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="12" cy="16" r="1" fill="currentColor" />
    </svg>
);

const ClockIcon = (props) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" {...props}>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const BoltIcon = (props) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M13 2L4 14h7l-2 8 9-12h-7l2-8z" />
    </svg>
);

const ShieldCheckIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const InfoIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   METHOD CARD
   ───────────────────────────────────────────────────────────── */

const MethodCard = memo(function MethodCard({
    icon: Icon, title, description, badge, badgeColor, badgeBg,
    accentColor, accentBg, onClick, isComingSoon,
}) {
    return (
        <Box
            p="18px"
            borderRadius="14px"
            border="2px solid"
            borderColor={isComingSoon ? BORDER : accentBg}
            bg={isComingSoon ? SURFACE_ALT : accentBg}
            cursor="pointer"
            position="relative"
            transition="all 0.2s ease"
            opacity={isComingSoon ? 0.7 : 1}
            _hover={{
                transform: 'translateY(-2px)',
                borderColor: isComingSoon ? '#CBD5E0' : accentColor,
                boxShadow: '0 6px 18px rgba(27,59,95,0.10)',
            }}
            onClick={onClick}
        >
            {badge && (
                <Box
                    position="absolute"
                    top="-10px"
                    right="14px"
                    bg={badgeBg}
                    color={badgeColor}
                    px="10px"
                    py="2px"
                    borderRadius="full"
                    fontSize="9px"
                    fontWeight="800"
                    letterSpacing="0.05em"
                    border="1.5px solid white"
                >
                    {badge}
                </Box>
            )}
            <HStack spacing="14px" align="start">
                <Flex
                    w="40px"
                    h="40px"
                    borderRadius="10px"
                    bg={isComingSoon ? BORDER : 'white'}
                    color={isComingSoon ? FAINT : accentColor}
                    align="center"
                    justify="center"
                    flexShrink={0}
                >
                    <Icon />
                </Flex>
                <VStack align="start" spacing="4px" flex="1">
                    <Text
                        fontSize="15px"
                        fontWeight="800"
                        color={isComingSoon ? MUTED : INK}
                        fontFamily={FONT}
                    >
                        {title}
                    </Text>
                    <Text fontSize="12px" color={MUTED} lineHeight="1.4">
                        {description}
                    </Text>
                </VStack>
            </HStack>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   TIER CARD (for tier upgrade modal)
   ───────────────────────────────────────────────────────────── */

const TierCard = memo(function TierCard({
    tier, name, limit, currencySymbol, requirements, isCurrent, isRecommended,
}) {
    return (
        <Box
            p="16px"
            borderRadius="12px"
            bg={isCurrent ? SUCCESS_BG : SURFACE}
            border="2px solid"
            borderColor={isCurrent ? SUCCESS : BORDER}
            position="relative"
            transition="all 0.2s ease"
            _hover={!isCurrent ? { borderColor: NAVY, transform: 'translateY(-2px)' } : {}}
        >
            {isCurrent && (
                <Box
                    position="absolute" top="-10px" right="14px"
                    bg={SUCCESS} color="white"
                    px="8px" py="2px" borderRadius="full"
                    fontSize="9px" fontWeight="800" letterSpacing="0.05em"
                >
                    YOUR TIER
                </Box>
            )}
            {!isCurrent && isRecommended && (
                <Box
                    position="absolute" top="-10px" right="14px"
                    bg={NAVY} color="white"
                    px="8px" py="2px" borderRadius="full"
                    fontSize="9px" fontWeight="800" letterSpacing="0.05em"
                >
                    RECOMMENDED
                </Box>
            )}
            <VStack align="start" spacing="10px">
                <Box>
                    <Text fontSize="9px" fontWeight="800" color={FAINT} letterSpacing="0.08em">
                        TIER {tier}
                    </Text>
                    <Heading
                        as="h4" fontSize="15px" fontWeight="800"
                        color={INK} fontFamily={FONT}
                    >
                        {name}
                    </Heading>
                </Box>
                <Box w="full" bg={isCurrent ? 'white' : SURFACE_ALT} p="10px" borderRadius="8px">
                    <Text fontSize="9px" fontWeight="700" color={FAINT} letterSpacing="0.06em" mb="2px">
                        WITHDRAWAL LIMIT
                    </Text>
                    <Text fontSize="16px" fontWeight="800" color={INK} fontFamily={FONT}>
                        {limit === Infinity ? 'Unlimited' : `${currencySymbol}${(limit / 1000).toFixed(0)}K`}
                    </Text>
                </Box>
                <Box w="full">
                    <Text fontSize="9px" fontWeight="700" color={FAINT} letterSpacing="0.06em" mb="6px">
                        REQUIREMENTS
                    </Text>
                    <VStack align="start" spacing="4px">
                        {requirements.map((req) => (
                            <HStack key={req.label} spacing="6px">
                                <Box
                                    w="6px" h="6px" borderRadius="full"
                                    bg={req.met ? SUCCESS : FAINT}
                                />
                                <Text
                                    fontSize="11px"
                                    color={req.met ? INK : MUTED}
                                    fontWeight={req.met ? '600' : '500'}
                                >
                                    {req.label}
                                </Text>
                            </HStack>
                        ))}
                    </VStack>
                </Box>
            </VStack>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   MAIN — WalletModals (renders the right modal for current step)
   ───────────────────────────────────────────────────────────── */

const WalletModals = ({ flow, wallet, currencySymbol }) => {
    const {
        step, method, amount, otp, resendTimer,
        sendingOtp, submitting, resultMessage, completedAmount,
        tierInfo,
        setAmount, setOtp,
        selectMethod, submitAmount, confirmSummary, submitEmbedlyPayout,
        resendOtp, goToVerification, close, OTP_LENGTH,
    } = flow;

    /* ── METHOD SELECTION ───────────────────────────────────── */
    if (step === FLOW_STEP.METHOD) {
        return (
            <Modal isCentered isOpen size="md" onClose={close}>
                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
                <ModalContent borderRadius="16px" fontFamily={FONT}>
                    <ModalCloseButton color={MUTED} _hover={{ bg: SURFACE_ALT, color: INK }} />
                    <ModalBody p="28px 24px">
                        <VStack spacing="20px" align="stretch">
                            <Box textAlign="center">
                                <Heading
                                    as="h2" fontSize="18px" fontWeight="800"
                                    color={INK} fontFamily={FONT} mb="4px"
                                >
                                    Choose withdrawal method
                                </Heading>
                                <Text fontSize="12px" color={MUTED}>
                                    Select how you'd like to receive your funds
                                </Text>
                            </Box>

                            <VStack spacing="12px" align="stretch">
                                <MethodCard
                                    icon={BoltIcon}
                                    title="Instant Withdrawal"
                                    description="Receive funds in your account within minutes. Requires OTP verification."
                                    badge="RECOMMENDED"
                                    badgeColor="white"
                                    badgeBg={NAVY}
                                    accentColor={NAVY}
                                    accentBg={CYAN}
                                    onClick={() => selectMethod(WITHDRAWAL_METHODS.EMBEDLY)}
                                />
                                <MethodCard
                                    icon={ClockIcon}
                                    title="Manual Transfer"
                                    description="Standard processing time: 24–48 hours. Currently in development."
                                    badge="COMING SOON"
                                    badgeColor="white"
                                    badgeBg={WARNING}
                                    accentColor={WARNING}
                                    accentBg={WARNING_BG}
                                    onClick={() => selectMethod(WITHDRAWAL_METHODS.MANUAL)}
                                    isComingSoon
                                />
                            </VStack>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    }

    /* ── COMING SOON (manual click) ─────────────────────────── */
    if (step === FLOW_STEP.COMING_SOON) {
        return (
            <Modal isCentered isOpen size="md" onClose={close}>
                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
                <ModalContent borderRadius="16px" fontFamily={FONT}>
                    <ModalCloseButton color={MUTED} _hover={{ bg: SURFACE_ALT, color: INK }} />
                    <ModalBody p="32px 24px">
                        <VStack spacing="16px" align="center">
                            <Flex
                                w="64px" h="64px" borderRadius="full"
                                bg={WARNING_BG} color={WARNING}
                                align="center" justify="center"
                            >
                                <ClockIcon />
                            </Flex>
                            <Box textAlign="center">
                                <Heading as="h2" fontSize="18px" fontWeight="800" color={INK} mb="6px" fontFamily={FONT}>
                                    Coming soon
                                </Heading>
                                <Text fontSize="13px" color={MUTED}>
                                    Manual withdrawals are currently in development. Use Instant Withdrawal for now.
                                </Text>
                            </Box>
                            <Button
                                w="full"
                                h="46px"
                                bg={NAVY}
                                color="white"
                                _hover={{ bg: NAVY_DEEP }}
                                onClick={() => selectMethod(WITHDRAWAL_METHODS.EMBEDLY)}
                                borderRadius="10px"
                                fontSize="13px"
                                fontWeight="700"
                                fontFamily={FONT}
                            >
                                Use Instant Withdrawal
                            </Button>
                            <Button
                                w="full"
                                h="40px"
                                variant="ghost"
                                color={MUTED}
                                onClick={close}
                                fontSize="12px"
                                fontWeight="600"
                            >
                                Maybe later
                            </Button>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    }

    /* ── AMOUNT INPUT ───────────────────────────────────────── */
    if (step === FLOW_STEP.AMOUNT) {
        const ledger = Number(wallet?.ledger_balance ?? 0);
        const exceedsBalance = amount > ledger;
        const isValid = amount >= 1 && !exceedsBalance;

        return (
            <Modal isCentered isOpen size="md" onClose={close}>
                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
                <ModalContent borderRadius="16px" fontFamily={FONT}>
                    <ModalCloseButton color={MUTED} _hover={{ bg: SURFACE_ALT, color: INK }} />
                    <ModalBody p="32px 24px">
                        <VStack spacing="16px" align="stretch">
                            <Box textAlign="center">
                                <Heading as="h2" fontSize="18px" fontWeight="800" color={INK} fontFamily={FONT} mb="6px">
                                    How much do you want to withdraw?
                                </Heading>
                                <Text fontSize="12px" color={MUTED}>
                                    Available balance:
                                    {' '}
                                    <Text as="span" fontWeight="700" color={INK}>
                                        {currencySymbol}{addCommasToNumber(ledger.toFixed(2))}
                                    </Text>
                                </Text>
                            </Box>

                            <NumberInput
                                value={amount || ''}
                                onChange={(_, v) => setAmount(Number.isFinite(v) ? v : 0)}
                                min={0}
                                max={ledger}
                            >
                                <NumberInputField
                                    placeholder={`Enter amount in ${currencySymbol}`}
                                    fontSize="18px"
                                    fontWeight="700"
                                    textAlign="center"
                                    h="56px"
                                    borderRadius="10px"
                                    border="2px solid"
                                    borderColor={exceedsBalance ? DANGER : BORDER}
                                    bg={SURFACE_ALT}
                                    fontFamily={FONT}
                                    _hover={{ borderColor: exceedsBalance ? DANGER : '#CBD5E0' }}
                                    _focus={{
                                        borderColor: exceedsBalance ? DANGER : NAVY,
                                        boxShadow: `0 0 0 3px ${exceedsBalance ? DANGER_BG : CYAN}`,
                                        bg: SURFACE,
                                    }}
                                />
                            </NumberInput>

                            {exceedsBalance && (
                                <Text fontSize="11px" color={DANGER} fontWeight="600" mt="-8px">
                                    Amount exceeds your available balance
                                </Text>
                            )}

                            {/* Tier hint */}
                            <Box
                                bg={INFO_BG}
                                border="1px solid"
                                borderColor={`${INFO}30`}
                                borderRadius="10px"
                                p="10px 12px"
                            >
                                <HStack align="start" spacing="8px">
                                    <Box color={INFO} mt="1px" flexShrink={0}>
                                        <InfoIcon />
                                    </Box>
                                    <Text fontSize="10.5px" color={INFO} lineHeight="1.5">
                                        Your current limit:{' '}
                                        <Text as="span" fontWeight="800">
                                            {tierInfo.currentLimit === Infinity
                                                ? 'Unlimited'
                                                : `${currencySymbol}${addCommasToNumber(tierInfo.currentLimit.toFixed(0))}`}
                                        </Text>
                                        {' '}(Tier {tierInfo.currentTier || 0}).
                                        {tierInfo.currentTier < 3 && ' Complete verification to increase.'}
                                    </Text>
                                </HStack>
                            </Box>

                            <Button
                                w="full"
                                h="48px"
                                bg={NAVY}
                                color="white"
                                _hover={{ bg: NAVY_DEEP }}
                                _disabled={{
                                    bg: BORDER, color: FAINT, cursor: 'not-allowed',
                                    _hover: { bg: BORDER },
                                }}
                                isDisabled={!isValid}
                                onClick={submitAmount}
                                borderRadius="10px"
                                fontSize="13px"
                                fontWeight="700"
                                fontFamily={FONT}
                            >
                                Continue
                            </Button>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    }

    /* ── TIER UPGRADE ───────────────────────────────────────── */
    if (step === FLOW_STEP.TIER_UPGRADE) {
        return (
            <Modal isCentered isOpen size={{ base: 'full', md: '3xl' }} onClose={close}>
                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
                <ModalContent
                    borderRadius={{ base: '0', md: '16px' }}
                    fontFamily={FONT}
                    maxH={{ base: '100vh', md: '90vh' }}
                >
                    <ModalCloseButton color={MUTED} _hover={{ bg: SURFACE_ALT, color: INK }} />
                    <ModalBody p={{ base: '24px 18px', md: '32px' }}>
                        <VStack spacing="20px" align="stretch">
                            <VStack spacing="10px">
                                <Flex
                                    w="60px" h="60px" borderRadius="full"
                                    bg={WARNING_BG} color={WARNING}
                                    align="center" justify="center"
                                >
                                    <LockIcon />
                                </Flex>
                                <Box textAlign="center">
                                    <Heading as="h2" fontSize="20px" fontWeight="800" color={INK} fontFamily={FONT} mb="4px">
                                        Increase your withdrawal limit
                                    </Heading>
                                    <Text fontSize="12.5px" color={MUTED} maxW="400px">
                                        Complete your account verification to unlock higher limits and stay compliant.
                                    </Text>
                                </Box>
                            </VStack>

                            <Grid
                                templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                                gap="12px"
                            >
                                <TierCard
                                    tier={1} name="Basic"
                                    limit={TIER_LIMITS.TIER_1_MAX}
                                    currencySymbol={currencySymbol}
                                    requirements={[{ label: 'NIN verified', met: tierInfo.hasNIN }]}
                                    isCurrent={tierInfo.currentTier === 1}
                                />
                                <TierCard
                                    tier={2} name="Standard"
                                    limit={TIER_LIMITS.TIER_2_MAX}
                                    currencySymbol={currencySymbol}
                                    requirements={[
                                        { label: 'NIN verified', met: tierInfo.hasNIN },
                                        { label: 'BVN verified', met: tierInfo.hasBVN },
                                    ]}
                                    isCurrent={tierInfo.currentTier === 2}
                                    isRecommended={tierInfo.currentTier < 2}
                                />
                                <TierCard
                                    tier={3} name="Premium"
                                    limit={Infinity}
                                    currencySymbol={currencySymbol}
                                    requirements={[
                                        { label: 'NIN verified', met: tierInfo.hasNIN },
                                        { label: 'BVN verified', met: tierInfo.hasBVN },
                                        { label: 'Proof of address', met: tierInfo.hasProof },
                                    ]}
                                    isCurrent={tierInfo.currentTier === 3}
                                    isRecommended={tierInfo.currentTier === 2}
                                />
                            </Grid>

                            <Box
                                bg={INFO_BG}
                                border="1px solid"
                                borderColor={`${INFO}30`}
                                p="10px 12px"
                                borderRadius="10px"
                            >
                                <HStack spacing="8px" align="start">
                                    <Box color={INFO} mt="1px" flexShrink={0}>
                                        <ShieldCheckIcon />
                                    </Box>
                                    <Text fontSize="10.5px" color={INFO} lineHeight="1.5">
                                        Your documents are encrypted and securely stored. We never share your data.
                                    </Text>
                                </HStack>
                            </Box>

                            <Button
                                w="full"
                                h="48px"
                                bg={NAVY}
                                color="white"
                                _hover={{ bg: NAVY_DEEP }}
                                onClick={goToVerification}
                                borderRadius="10px"
                                fontSize="13px"
                                fontWeight="700"
                                fontFamily={FONT}
                            >
                                Complete verification now
                            </Button>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    }

    /* ── SUMMARY ────────────────────────────────────────────── */
    if (step === FLOW_STEP.SUMMARY) {
        const ledger = Number(wallet?.ledger_balance ?? 0);
        const remaining = ledger - amount;

        return (
            <Modal isCentered isOpen size="md" onClose={close}>
                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
                <ModalContent borderRadius="16px" fontFamily={FONT}>
                    <ModalCloseButton color={MUTED} _hover={{ bg: SURFACE_ALT, color: INK }} />
                    <ModalBody p="28px 24px">
                        <VStack spacing="20px" align="stretch">
                            <Box textAlign="center">
                                <Text fontSize="11px" color={MUTED} fontWeight="700" letterSpacing="0.08em" mb="6px">
                                    WITHDRAWAL AMOUNT
                                </Text>
                                <Heading as="h2" fontSize="32px" fontWeight="800" color={NAVY} fontFamily={FONT} lineHeight="1">
                                    {currencySymbol}{addCommasToNumber(amount.toFixed(2))}
                                </Heading>
                                <Flex justify="center" mt="10px">
                                    <Box
                                        bg={method === WITHDRAWAL_METHODS.EMBEDLY ? CYAN : WARNING_BG}
                                        color={method === WITHDRAWAL_METHODS.EMBEDLY ? NAVY : WARNING}
                                        px="10px" py="3px"
                                        borderRadius="full"
                                        fontSize="10px"
                                        fontWeight="800"
                                        letterSpacing="0.05em"
                                    >
                                        {method === WITHDRAWAL_METHODS.EMBEDLY
                                            ? 'INSTANT • EMBEDLY'
                                            : 'MANUAL • 24-48 HRS'}
                                    </Box>
                                </Flex>
                            </Box>

                            <Box
                                bg={SURFACE_ALT}
                                border="1px solid"
                                borderColor={BORDER}
                                borderRadius="12px"
                                p="16px"
                            >
                                <VStack spacing="10px" align="stretch">
                                    <HStack justify="space-between">
                                        <Text fontSize="12px" color={MUTED}>Current balance</Text>
                                        <Text fontSize="12px" fontWeight="700" color={INK}>
                                            {currencySymbol}{addCommasToNumber(ledger.toFixed(2))}
                                        </Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text fontSize="12px" color={MUTED}>Withdrawal</Text>
                                        <Text fontSize="12px" fontWeight="700" color={DANGER}>
                                            -{currencySymbol}{addCommasToNumber(amount.toFixed(2))}
                                        </Text>
                                    </HStack>
                                    <Divider borderColor={BORDER} />
                                    <HStack justify="space-between">
                                        <Text fontSize="13px" fontWeight="800" color={INK}>Remaining</Text>
                                        <Text fontSize="14px" fontWeight="800" color={NAVY} fontFamily={FONT}>
                                            {currencySymbol}{addCommasToNumber(remaining.toFixed(2))}
                                        </Text>
                                    </HStack>
                                </VStack>
                            </Box>

                            <Box
                                bg={INFO_BG}
                                border="1px solid"
                                borderColor={`${INFO}30`}
                                p="10px 12px"
                                borderRadius="10px"
                            >
                                <HStack spacing="8px" align="start">
                                    <Box color={INFO} mt="1px" flexShrink={0}>
                                        <InfoIcon />
                                    </Box>
                                    <Text fontSize="10.5px" color={INFO} lineHeight="1.5">
                                        {method === WITHDRAWAL_METHODS.EMBEDLY
                                            ? 'Funds will be sent to your registered bank account after OTP verification.'
                                            : 'Funds will be sent to your registered bank account within 24–48 hours.'}
                                    </Text>
                                </HStack>
                            </Box>

                            <VStack spacing="10px">
                                <Button
                                    w="full"
                                    h="48px"
                                    bg={NAVY}
                                    color="white"
                                    _hover={{ bg: NAVY_DEEP }}
                                    onClick={confirmSummary}
                                    isLoading={submitting}
                                    loadingText="Processing…"
                                    isDisabled={submitting}
                                    borderRadius="10px"
                                    fontSize="13px"
                                    fontWeight="700"
                                    fontFamily={FONT}
                                >
                                    {method === WITHDRAWAL_METHODS.EMBEDLY ? 'Continue to OTP' : 'Confirm withdrawal'}
                                </Button>
                                <Button
                                    w="full"
                                    h="44px"
                                    variant="ghost"
                                    color={MUTED}
                                    onClick={close}
                                    isDisabled={submitting}
                                    fontSize="12px"
                                    fontWeight="600"
                                >
                                    Cancel
                                </Button>
                            </VStack>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    }

    /* ── OTP ────────────────────────────────────────────────── */
    if (step === FLOW_STEP.OTP) {
        return (
            <Modal isCentered isOpen size="md" onClose={close}>
                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
                <ModalContent borderRadius="16px" fontFamily={FONT}>
                    <ModalCloseButton color={MUTED} _hover={{ bg: SURFACE_ALT, color: INK }} />
                    <ModalBody p="28px 24px">
                        <VStack spacing="18px" align="stretch">
                            <VStack spacing="10px">
                                <Flex
                                    w="56px" h="56px" borderRadius="full"
                                    bg={CYAN} color={NAVY}
                                    align="center" justify="center"
                                >
                                    <BoltIcon />
                                </Flex>
                                <Box textAlign="center">
                                    <Heading as="h2" fontSize="18px" fontWeight="800" color={INK} fontFamily={FONT} mb="4px">
                                        Enter OTP code
                                    </Heading>
                                    <Text fontSize="12px" color={MUTED}>
                                        We sent a {OTP_LENGTH}-digit code to your registered email and phone.
                                    </Text>
                                </Box>
                            </VStack>

                            <HStack spacing="10px" justify="center">
                                <PinInput
                                    otp
                                    size="lg"
                                    value={otp}
                                    onChange={setOtp}
                                    autoFocus
                                >
                                    {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                                        <PinInputField
                                            key={i}
                                            borderRadius="10px"
                                            border="2px solid"
                                            borderColor={BORDER}
                                            bg={SURFACE_ALT}
                                            fontFamily={FONT}
                                            fontSize="20px"
                                            fontWeight="800"
                                            color={INK}
                                            _hover={{ borderColor: '#CBD5E0' }}
                                            _focus={{ borderColor: NAVY, boxShadow: `0 0 0 3px ${CYAN}`, bg: SURFACE }}
                                        />
                                    ))}
                                </PinInput>
                            </HStack>

                            <HStack justify="center" spacing="6px">
                                <Text fontSize="12px" color={MUTED}>Didn't receive code?</Text>
                                <Button
                                    variant="link"
                                    onClick={resendOtp}
                                    isDisabled={resendTimer > 0 || sendingOtp}
                                    isLoading={sendingOtp}
                                    color={resendTimer > 0 ? FAINT : NAVY}
                                    fontSize="12px"
                                    fontWeight="700"
                                    _hover={{ textDecoration: 'underline' }}
                                >
                                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                                </Button>
                            </HStack>

                            <Box
                                bg={CYAN}
                                border="1px solid"
                                borderColor={`${NAVY}20`}
                                p="12px"
                                borderRadius="10px"
                            >
                                <Text fontSize="9px" fontWeight="800" color={FAINT} letterSpacing="0.08em" mb="8px">
                                    WITHDRAWAL DETAILS
                                </Text>
                                <HStack justify="space-between" mb="4px">
                                    <Text fontSize="11px" color={MUTED}>Amount</Text>
                                    <Text fontSize="12px" fontWeight="800" color={INK}>
                                        {currencySymbol}{addCommasToNumber(amount.toFixed(2))}
                                    </Text>
                                </HStack>
                                <HStack justify="space-between">
                                    <Text fontSize="11px" color={MUTED}>Method</Text>
                                    <Text fontSize="12px" fontWeight="700" color={INK}>Instant (Embedly)</Text>
                                </HStack>
                            </Box>

                            <VStack spacing="10px">
                                <Button
                                    w="full"
                                    h="48px"
                                    bg={NAVY}
                                    color="white"
                                    _hover={{ bg: NAVY_DEEP }}
                                    onClick={submitEmbedlyPayout}
                                    isLoading={submitting}
                                    loadingText="Verifying…"
                                    isDisabled={otp.length !== OTP_LENGTH || submitting}
                                    _disabled={{
                                        bg: BORDER, color: FAINT, cursor: 'not-allowed',
                                        _hover: { bg: BORDER },
                                    }}
                                    borderRadius="10px"
                                    fontSize="13px"
                                    fontWeight="700"
                                    fontFamily={FONT}
                                >
                                    Verify & withdraw
                                </Button>
                                <Button
                                    w="full"
                                    h="44px"
                                    variant="ghost"
                                    color={MUTED}
                                    onClick={close}
                                    isDisabled={submitting}
                                    fontSize="12px"
                                    fontWeight="600"
                                >
                                    Cancel
                                </Button>
                            </VStack>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    }

    /* ── SUCCESS / ERROR ────────────────────────────────────── */
    if (step === FLOW_STEP.SUCCESS || step === FLOW_STEP.ERROR) {
        const isSuccess = step === FLOW_STEP.SUCCESS;

        return (
            <Modal isCentered isOpen size="sm" onClose={close}>
                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
                <ModalContent borderRadius="16px" fontFamily={FONT}>
                    <ModalBody p="32px 24px">
                        <VStack spacing="16px">
                            <Box>
                                {isSuccess ? <CheckCircleIcon /> : <XCircleIcon />}
                            </Box>
                            <Box textAlign="center">
                                <Heading as="h2" fontSize="18px" fontWeight="800" color={INK} fontFamily={FONT} mb="6px">
                                    {isSuccess ? 'Withdrawal submitted' : 'Withdrawal failed'}
                                </Heading>
                                <Text fontSize="12.5px" color={MUTED} lineHeight="1.5">
                                    {resultMessage}
                                </Text>
                            </Box>

                            {isSuccess && (
                                <Box
                                    w="full"
                                    bg={SURFACE_ALT}
                                    border="1px solid"
                                    borderColor={BORDER}
                                    p="14px"
                                    borderRadius="10px"
                                >
                                    <VStack spacing="8px" align="stretch">
                                        <HStack justify="space-between">
                                            <Text fontSize="11px" color={MUTED}>Amount</Text>
                                            <Text fontSize="12px" fontWeight="800" color={INK}>
                                                {currencySymbol}{addCommasToNumber((completedAmount || 0).toFixed(2))}
                                            </Text>
                                        </HStack>
                                        <HStack justify="space-between">
                                            <Text fontSize="11px" color={MUTED}>Method</Text>
                                            <Text fontSize="12px" fontWeight="700" color={INK} textTransform="capitalize">
                                                {method ?? 'Embedly'}
                                            </Text>
                                        </HStack>
                                        <HStack justify="space-between">
                                            <Text fontSize="11px" color={MUTED}>Status</Text>
                                            <Text fontSize="11px" fontWeight="800" color={WARNING}>
                                                Processing
                                            </Text>
                                        </HStack>
                                    </VStack>
                                </Box>
                            )}

                            <Button
                                w="full"
                                h="46px"
                                bg={NAVY}
                                color="white"
                                _hover={{ bg: NAVY_DEEP }}
                                onClick={close}
                                borderRadius="10px"
                                fontSize="13px"
                                fontWeight="700"
                                fontFamily={FONT}
                            >
                                Done
                            </Button>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    }

    return null;
};

export default WalletModals;